import { useFeatureGate as useFeatureGateBase } from '@statsig/react-bindings';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { useParams as useParamsBase } from 'react-router-dom';

import { Logger, logger as loggerBase } from '../../datadog/logger';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { useIsScreenBelowBreakpoint as useIsScreenBelowBreakpointBase } from '../../hooks/useIsScreenBelowBreakpoint';
import { useTrackEvent as useTrackEventBase } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useGetClientSafetyGuard as useGetClientSafetyGuardBase } from '../../useGetClientSafetyGuard';
import { useGetShouldShowPatientAVS as useGetShouldShowPatientAVSBase } from '../reflections/useGetShouldShowPatientAVS';
import { useGetShouldShowReflectionTopics as useGetShouldShowReflectionTopicsBase } from '../reflections/useGetShouldShowReflectionTopics';
import {
  OutstandingMeasures,
  VisitorDrawerState,
  didManuallyResolveSoftBlockAtom,
  visitorDrawerStateAtom,
  visitorMeasuresAtom,
  wasMeasuresDrawerAutoOpenedAtom,
} from '../state';

/**
 * Time to wait before resolving decision to auto-open the pre-session drawer.
 * This buffer gives the client's device additional time to await the response
 * from the outstanding measures and client safety guard queries, if they are
 * still in flight, but with a hard deadline so as to prevent a jarring
 * experience of the drawer opening long after the client has already loaded
 * into the waiting room.
 *
 * - If we have not completed the client safety guard query before the deadline,
 *   we will NOT open the drawer to err on the side of caution.
 * - If we have confirmed the client does not require safety guards, but we have
 *   not completed the outstanding measures query before the deadline, we will
 *   open the drawer optimistically to a loading state. The client will see the
 *   measures completed screen if it eventually turns out they have no
 *   outstanding measures.
 * - If we have completed both queries before the deadline, we will open the
 *   drawer using the results of the queries.
 */
export const DRAWER_OPEN_BUFFER_MS = 1500;

type Props = {
  /** Optional callback to call when the drawer is auto-opened. */
  onAutoOpen?: () => void;
};

export type Dependencies = {
  logger: Logger;
  useFeatureGate: typeof useFeatureGateBase;
  useGetClientSafetyGuard: typeof useGetClientSafetyGuardBase;
  useGetShouldShowPatientAVS: typeof useGetShouldShowPatientAVSBase;
  useGetShouldShowReflectionTopics: typeof useGetShouldShowReflectionTopicsBase;
  useIsScreenBelowBreakpoint: typeof useIsScreenBelowBreakpointBase;
  useParams: typeof useParamsBase;
  useTrackEvent: typeof useTrackEventBase;
};

const DEFAULT_DEPENDENCIES: Dependencies = {
  logger: loggerBase,
  useFeatureGate: useFeatureGateBase,
  useGetClientSafetyGuard: useGetClientSafetyGuardBase,
  useGetShouldShowPatientAVS: useGetShouldShowPatientAVSBase,
  useGetShouldShowReflectionTopics: useGetShouldShowReflectionTopicsBase,
  useIsScreenBelowBreakpoint: useIsScreenBelowBreakpointBase,
  useParams: useParamsBase,
  useTrackEvent: useTrackEventBase,
};

/**
 * Utility hook to auto-open the pre-session drawer when a client joins the
 * waiting room. The drawer opens to outstanding measures (if any), or
 * reflection topics (if the client has session topics, or a shared AVS and is
 * in the session recap experiment, and has no outstanding measures), provided
 * the screen is large enough for the split-screen push drawer layout (>= sm
 * breakpoint), and the client does not require safety guards.
 *
 * Since this hook may run before we've finished fetching the necessary data to
 * determine if the drawer should be opened (especially for logged in clients),
 * it schedules a timeout to re-check these conditions after a small buffer
 * period if we don't have all the necessary data on first run, to avoid a
 * jarring experience of the drawer opening several seconds *after* the client
 * loads into the waiting room.
 *
 * Fires at most once per mount.
 *
 * @param props - Primary props for the hook:
 * - onAutoOpen: Optional callback to call when the drawer is auto-opened.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 */
export function useAutoOpenPreSessionDrawer(
  { onAutoOpen }: Props = {},
  {
    logger,
    useGetClientSafetyGuard,
    useGetShouldShowPatientAVS,
    useGetShouldShowReflectionTopics,
    useIsScreenBelowBreakpoint,
    useParams,
    useTrackEvent,
  }: Dependencies = DEFAULT_DEPENDENCIES,
) {
  const { userViewed } = useTrackEvent();
  const { patientShortId = '' } = useParams();

  const getDrawerState = useAtomCallbackMemoized((get) => get(visitorDrawerStateAtom), []);
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const setWasMeasuresDrawerAutoOpened = useSetAtom(wasMeasuresDrawerAutoOpenedAtom);
  const hasAutoOpenedRef = useRef(false);

  const { getRequiresSafetyGuards } = useGetClientSafetyGuard();
  const { getShouldShowReflectionTopics } = useGetShouldShowReflectionTopics();
  const { isLoading: isPatientAVSLoading, getShouldShowPatientAVS } = useGetShouldShowPatientAVS();
  const isBelowSmBreakpoint = useIsScreenBelowBreakpoint('sm');
  const getVisitorMeasures = useAtomCallbackMemoized((get) => get(visitorMeasuresAtom), []);
  const getDidManuallyResolveSoftBlock = useAtomCallbackMemoized((get) => get(didManuallyResolveSoftBlockAtom), []);

  const maybeOpenDrawer = useCallback(() => {
    if (hasAutoOpenedRef.current) return;
    if (!patientShortId) return;

    const visitorMeasures = getVisitorMeasures();
    const requiresSafetyGuards = getRequiresSafetyGuards();

    // Do not auto-open if the client deliberately resolved the pre-session soft
    // block this session by skipping or submitting their measures. They've
    // already been prompted to complete measures before their session, so we
    // suppress the waiting-room auto-open to avoid prompting them twice. Clients
    // who were auto-transitioned because they ran out of time are intentionally
    // excluded here, so they still get prompted in the waiting room.
    if (getDidManuallyResolveSoftBlock()) {
      logSkippedAutoOpen(
        logger,
        'client manually resolved the pre-session soft block',
        patientShortId,
        visitorMeasures,
      );
      return;
    }

    // Do not open the drawer if the client requires safety guards, erring on
    // the side of caution for clients whose safety guard status is unknown.
    if (requiresSafetyGuards !== false) {
      logSkippedAutoOpen(
        logger,
        requiresSafetyGuards ? 'client requires safety guards' : 'client safety guard status is unknown',
        patientShortId,
        visitorMeasures,
      );
      return;
    }

    // Do not open the drawer if it's already open.
    const drawerState = getDrawerState();
    if (drawerState) {
      logSkippedAutoOpen(logger, 'drawer already open', patientShortId, visitorMeasures, { drawerState });
      return;
    }

    // We'll set this ref even if we skip auto-opening due to screen size or
    // having no outstanding measures, to make future effect runs
    // short-circuit sooner.
    hasAutoOpenedRef.current = true;

    if (isBelowSmBreakpoint) {
      logSkippedAutoOpen(logger, 'screen too small for split-screen', patientShortId, visitorMeasures);
      return;
    }

    // If the measures query already resolved with nothing outstanding, open the
    // reflections drawer if the client has reflection topics, or has a shared
    // AVS and is in the session recap experiment, otherwise skip. The
    // experiment is only checked when there are no topics. When measures are
    // still loading (undefined), we optimistically open to avoid a jarring
    // late-open on slower networks/devices.
    if (visitorMeasures && !visitorMeasures.length) {
      if (getShouldShowReflectionTopics() || getShouldShowPatientAVS()) {
        setDrawerState(VisitorDrawerState.REFLECTION_TOPICS);
        logAutoOpenReflectionTopics(logger, patientShortId);
        onAutoOpen?.();
      } else {
        logSkippedAutoOpen(logger, 'no outstanding measures or reflection topics', patientShortId, visitorMeasures);
      }
      return;
    }

    setWasMeasuresDrawerAutoOpened(true);
    setDrawerState(VisitorDrawerState.PENDING_FORMS);
    logAutoOpen(logger, patientShortId, visitorMeasures);
    onAutoOpen?.();
    userViewed(EventTypeEnum.CLIENT_MEASURES_AUTO_OPENED, {
      patientShortId,
      outstandingMeasureTypes: visitorMeasures?.map((m) => m.schema.type) ?? [],
    });
  }, [
    getRequiresSafetyGuards,
    getDrawerState,
    getDidManuallyResolveSoftBlock,
    getShouldShowPatientAVS,
    getShouldShowReflectionTopics,
    getVisitorMeasures,
    hasAutoOpenedRef,
    isBelowSmBreakpoint,
    logger,
    onAutoOpen,
    patientShortId,
    setDrawerState,
    setWasMeasuresDrawerAutoOpened,
    userViewed,
  ]);

  useEffect(
    function autoOpenPreSessionDrawerEffect() {
      if (hasAutoOpenedRef.current) return;
      if (!patientShortId) return;

      let timeout: NodeJS.Timeout | undefined;

      const visitorMeasures = getVisitorMeasures();
      const requiresSafetyGuards = getRequiresSafetyGuards();
      const measuresLoadedEmpty = visitorMeasures != null && visitorMeasures.length === 0;
      const isReflectionsContentLoading = getShouldShowReflectionTopics() === undefined || isPatientAVSLoading;

      // If measures or client safety guards are still loading, give them a
      // chance to resolve before making a decision. If the client has no
      // outstanding measures but the reflection topics query (or, without
      // topics, the shared AVS query) is still loading, we also defer for it
      // to try to finish first.
      if (
        !visitorMeasures ||
        requiresSafetyGuards === undefined ||
        (measuresLoadedEmpty && isReflectionsContentLoading)
      ) {
        logger.info('Data is still loading, deferring auto-open decision', {
          patientShortId,
          bufferMs: DRAWER_OPEN_BUFFER_MS,
        });

        timeout = setTimeout(maybeOpenDrawer, DRAWER_OPEN_BUFFER_MS);
      } else {
        maybeOpenDrawer();
      }

      return function cleanup() {
        clearTimeout(timeout);
      };
    },
    // NOTE: These dependencies should be as stable as possible to avoid re-running the effect unnecessarily.
    [
      getRequiresSafetyGuards,
      getShouldShowReflectionTopics,
      getVisitorMeasures,
      isPatientAVSLoading,
      logger,
      maybeOpenDrawer,
      patientShortId,
    ],
  );
}

function logSkippedAutoOpen(
  logger: Logger,
  reason: string,
  patientShortId: string,
  outstandingMeasures: OutstandingMeasures | undefined,
  otherProps: Record<string, unknown> = {},
) {
  logger.info(`Skipping auto-open of pre-session drawer: ${reason}`, {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
    ...otherProps,
  });
}

function logAutoOpen(logger: Logger, patientShortId: string, outstandingMeasures: OutstandingMeasures | undefined) {
  logger.info('Auto-opening measures drawer', {
    patientShortId,
    outstandingMeasuresLoaded: outstandingMeasures !== undefined,
    outstandingMeasuresCount: outstandingMeasures?.length ?? 0,
  });
}

function logAutoOpenReflectionTopics(logger: Logger, patientShortId: string) {
  logger.info('Auto-opening reflection topics drawer', { patientShortId });
}
